<script setup lang="ts">
  import { computed, getCurrentInstance } from 'vue';
  import PosUiButton from '@/components/pos-v2/ui/PosUiButton.vue';
  import PosUiEmptyState from '@/components/pos-v2/ui/PosUiEmptyState.vue';
  import {
    isRetryableReadReason,
    readReasonKey,
  } from '@/domain/menu-import/workspace-view';

  /**
   * The read failed and nothing was built (plan §4.4 «Failed»): why, and the
   * ways on: new photos, typing the menu, building it by hand, and «Intentar
   * de nuevo» (the same pages) when the failure was on our side. With the AI
   * off only «Cargarlo a mano» is left. Never placeholder rows.
   */
  const props = withDefaults(
    defineProps<{
      reason?: string | null;
      aiEnabled: boolean;
      /** A way on is running. */
      disabled?: boolean;
      /** Offline: the ways that call the server wait. */
      online?: boolean;
    }>(),
    { reason: null, disabled: false, online: true }
  );
  const emit = defineEmits<{
    (e: 'retake'): void;
    (e: 'type'): void;
    (e: 'manual'): void;
    (e: 'retry'): void;
  }>();
  const proxy = getCurrentInstance()!.proxy;

  const description = computed(() => {
    const key = readReasonKey(props.reason);
    return key === null ? undefined : String(proxy.$t(key));
  });
  const retryable = computed(
    () => props.aiEnabled && isRetryableReadReason(props.reason)
  );
</script>

<template>
  <PosUiEmptyState
    class="v2-menu-read-failed"
    icon="image-off-outline"
    :title="$t('menuImport.failed.title')"
    :description="description"
  >
    <template #action>
      <div class="failed-actions">
        <PosUiButton
          v-if="retryable"
          color="neutral"
          icon="refresh"
          block
          :disabled="disabled || !online"
          @click="emit('retry')"
          >{{ $t('menuImport.failed.retry') }}</PosUiButton
        >
        <PosUiButton
          v-if="aiEnabled"
          color="neutral"
          :variant="retryable ? 'outline' : 'solid'"
          icon="camera-outline"
          block
          :disabled="disabled"
          @click="emit('retake')"
          >{{ $t('menuImport.failed.retake') }}</PosUiButton
        >
        <PosUiButton
          v-if="aiEnabled"
          color="neutral"
          variant="outline"
          icon="text-box-outline"
          block
          :disabled="disabled"
          @click="emit('type')"
          >{{ $t('menuImport.source.typeTitle') }}</PosUiButton
        >
        <PosUiButton
          color="neutral"
          :variant="aiEnabled ? 'ghost' : 'solid'"
          icon="pencil-outline"
          block
          :disabled="disabled || !online"
          @click="emit('manual')"
          >{{ $t('menuImport.source.manualTitle') }}</PosUiButton
        >
      </div>
    </template>
  </PosUiEmptyState>
</template>

<style scoped lang="scss">
  .v2-menu-read-failed {
    @apply tw-rounded-xl tw-border tw-border-solid tw-border-neutral-200 tw-bg-white;
    .failed-actions {
      @apply tw-flex tw-w-full tw-min-w-[16rem] tw-flex-col tw-gap-2;
    }
  }
</style>
